import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex w-full min-w-0 flex-col gap-3 rounded-2xl bg-white p-3 shadow-lg lg:inline-flex lg:w-fit lg:max-w-[calc(100vw-3rem)] lg:flex-row lg:items-center lg:gap-2 lg:rounded-full"
}
const _hoisted_2 = { key: 0 }
const _hoisted_3 = ["x-trap"]
const _hoisted_4 = { key: 0 }
const _hoisted_5 = ["x-trap"]

import { computed, ref, onMounted, onUnmounted, nextTick } from "vue";
import { storeToRefs } from "pinia";

import { getTranslation, getGlobalTranslation } from "~/js/composables/translations";

import { Translations } from "~/js/interfaces/translations";
import { Facet, FacetCount } from "~/js/interfaces/facets";

import { useLocationsStore } from "~/js/store/locations";
import { countActiveLocationFilters } from "~/js/composables/location-filter-count";

import FilterLocationSearch from "~/vue/molecules/filters/FilterLocationSearch.vue";
import FilterLocationCountry from "~/vue/molecules/filters/FilterLocationCountry.vue";
import FilterLocationsPanel from "~/vue/molecules/filters/FilterLocationsPanel.vue";
import ButtonFilterLocations from "~/vue/atoms/buttons/ButtonFilterLocations.vue";
import SortLocations from "~/vue/organisms/filters/SortLocations.vue";

interface Props {
	translations?: Translations | null;
	/** search-bar: floating pill; header: filters button + panel; mobile: stacked controls */
	variant?: "search-bar" | "header" | "mobile";
}


export default /*@__PURE__*/_defineComponent({
  __name: 'FilterLocations',
  props: {
    translations: { default: null },
    variant: { default: "mobile" }
  },
  emits: ["expanded-change"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const locationsStore = useLocationsStore();
const { facets, loading, filters } = storeToRefs(locationsStore);
const filtersExpanded = ref(false);
const isDesktopViewport = ref(typeof window !== "undefined" ? window.innerWidth >= 1024 : false);
const headerRootRef = ref<HTMLElement | null>(null);
const desktopPanelStyle = ref<Record<string, string>>({});

const updateViewport = () => {
	if (typeof window === "undefined") return;
	isDesktopViewport.value = window.innerWidth >= 1024;
	updateDesktopPanelPosition();
};

const updateDesktopPanelPosition = () => {
	if (!headerRootRef.value || typeof window === "undefined") return;
	const rect = headerRootRef.value.getBoundingClientRect();
	desktopPanelStyle.value = {
		left: `${Math.round(rect.left)}px`,
		top: `${Math.round(rect.bottom + 12)}px`,
	};
};

onMounted(() => {
	updateViewport();
	if (typeof window !== "undefined") {
		window.addEventListener("resize", updateViewport);
		window.addEventListener("scroll", updateDesktopPanelPosition, true);
	}
});

onUnmounted(() => {
	if (typeof window !== "undefined") {
		window.removeEventListener("resize", updateViewport);
		window.removeEventListener("scroll", updateDesktopPanelPosition, true);
	}
});

const toggleFilters = () => {
	filtersExpanded.value = !filtersExpanded.value;
	emit("expanded-change", filtersExpanded.value);
	if (filtersExpanded.value) {
		void nextTick(() => {
			updateDesktopPanelPosition();
		});
	}
};

const onBackdropClick = (event: MouseEvent) => {
	toggleFilters();
};

const onSheetClick = (event: MouseEvent) => {
	event.stopPropagation();
};

const checkedValue = (fieldName: string) => {
	return (filters.value[fieldName]?.value ?? null) ? filters.value[fieldName].value : false;
};

const checkTypeValue = (fieldName: string, value: string) => {
	return (filters.value[fieldName]?.values ?? null) ? filters.value[fieldName].values.indexOf(value) > -1 : false;
};

const countrySelected = (value: string) => {
	return filters.value.countries.values.includes(value);
};

const createFilter = (fieldName: string) => {
	const facet = facets.value.find((facet: Facet) => facet.field_name === fieldName);

	if (facet && fieldName === "country") {
		facet.counts.forEach((count: FacetCount) => {
			count.value = getTranslation(props.translations, count.highlighted);
		});
	}

	return facet;
};

const countryFacet = computed(() => createFilter("country"));

const orderedCountries = computed(() => {
	if (!countryFacet.value) {
		return [];
	}

	return [...countryFacet.value.counts].sort((a: FacetCount, b: FacetCount) =>
		a.highlighted < b.highlighted ? -1 : b.highlighted < a.highlighted ? 1 : 0,
	);
});

const filtersSelected = computed(() =>
	filters.value ? countActiveLocationFilters(filters.value) : 0,
);

const selectedCountry = computed({
	get: () => filters.value.countries.values[0] ?? "",
	set: (value: string) => {
		locationsStore.setCountryFilter(value || null);
	},
});

const updateFacet = (facet: { field: string; value: string | boolean }) => {
	locationsStore.setFilter(facet.field as keyof typeof filters.value, facet.value as string);
};

const searchPlaceholder = computed(() => getTranslation(props.translations, "Search locations"));

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (__props.variant === 'search-bar')
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(FilterLocationSearch, {
            label: searchPlaceholder.value,
            loading: _unref(loading),
            utilities: "w-full lg:shrink-0 lg:w-[15rem] xl:w-[17rem]"
          }, null, 8, ["label", "loading"]),
          _createElementVNode("div", {
            ref_key: "headerRootRef",
            ref: headerRootRef,
            class: "flex w-full min-w-0 flex-row gap-2 lg:w-auto lg:justify-start"
          }, [
            _createVNode(FilterLocationCountry, {
              label: _unref(getTranslation)(__props.translations, 'Country'),
              loading: _unref(loading),
              options: orderedCountries.value,
              selected: selectedCountry.value,
              utilities: "min-w-0 flex-1 lg:flex-none lg:w-[10rem] xl:w-[12rem]",
              onSelect: _cache[0] || (_cache[0] = ($event: any) => (selectedCountry.value = $event))
            }, null, 8, ["label", "loading", "options", "selected"]),
            _createVNode(ButtonFilterLocations, {
              label: _unref(getGlobalTranslation)('Filters'),
              selected: filtersSelected.value,
              loading: _unref(loading),
              utilities: "w-auto flex-none lg:w-auto",
              onClick: toggleFilters
            }, null, 8, ["label", "selected", "loading"])
          ], 512)
        ]))
      : _createCommentVNode("", true),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (filtersExpanded.value && isDesktopViewport.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createElementVNode("div", {
              class: "fixed inset-0 z-[2490]",
              onClick: toggleFilters
            }),
            _createElementVNode("div", {
              style: _normalizeStyle(desktopPanelStyle.value),
              class: "fixed z-[2500] flex max-h-[min(34rem,calc(100vh-14rem))] w-[22rem] flex-col overflow-hidden rounded-2xl bg-white p-6 shadow-xl",
              "x-trap": filtersExpanded.value
            }, [
              _createVNode(FilterLocationsPanel, {
                translations: __props.translations,
                loading: _unref(loading),
                "distance-label": _unref(getTranslation)(__props.translations, 'Distance'),
                "checked-value": checkedValue,
                "check-type-value": checkTypeValue,
                onClose: toggleFilters,
                onClearAll: _cache[1] || (_cache[1] = ($event: any) => (_unref(locationsStore).clearFilters())),
                onSetFacet: _cache[2] || (_cache[2] = ($event: any) => (updateFacet($event)))
              }, null, 8, ["translations", "loading", "distance-label"])
            ], 12, _hoisted_3)
          ]))
        : _createCommentVNode("", true)
    ])),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (filtersExpanded.value && !isDesktopViewport.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", {
              class: "fixed inset-0 z-[2100] bg-black/40",
              onClick: onBackdropClick
            }),
            _createElementVNode("div", {
              "x-trap": filtersExpanded.value,
              class: "fixed bottom-0 left-0 z-[2200] h-[85vh] max-h-[85vh] w-full overflow-hidden rounded-t-3xl bg-white p-6 shadow-2xl",
              onClick: onSheetClick
            }, [
              _createVNode(FilterLocationsPanel, {
                translations: __props.translations,
                loading: _unref(loading),
                "distance-label": _unref(getTranslation)(__props.translations, 'Distance'),
                "checked-value": checkedValue,
                "check-type-value": checkTypeValue,
                onClose: toggleFilters,
                onClearAll: _cache[3] || (_cache[3] = ($event: any) => (_unref(locationsStore).clearFilters())),
                onSetFacet: _cache[4] || (_cache[4] = ($event: any) => (updateFacet($event)))
              }, null, 8, ["translations", "loading", "distance-label"])
            ], 8, _hoisted_5)
          ]))
        : _createCommentVNode("", true)
    ])),
    (__props.variant === 'header')
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          ref_key: "headerRootRef",
          ref: headerRootRef,
          class: "relative shrink-0"
        }, [
          _createVNode(SortLocations, { translations: __props.translations }, null, 8, ["translations"])
        ], 512))
      : _createCommentVNode("", true)
  ], 64))
}
}

})